import axios, { AxiosError } from "axios"
import { Dispatch, RNBThunkAction } from "../store.config"
import { isAxiosError } from "../../utils/asyncTools"
import { ToastErrorProps } from "./merchantCodes.ducks"

type User = {
  bank_user_id: number
  email: string
  created_at: string
}

export interface StatusJedeclare {
  connected: boolean
  user: User | null
  connected_companies_ids: number[]
}

const enum JedeclareActionsEnum {
  CONNECT_JEDECLARE_USER_ATTEMPT = "JEDECLARE/connectJedeclareUserAttempt",
  CONNECT_JEDECLARE_USER_SUCCESS = "JEDECLARE/connectJedeclareUserSuccess",
  CONNECT_JEDECLARE_USER_ERROR = "JEDECLARE/connectJedeclareUserError",

  GET_JEDECLARE_STATUS_ATTEMPT = "JEDECLARE/getJedeclareStatusAttempt",
  GET_JEDECLARE_STATUS_SUCCESS = "JEDECLARE/getJedeclareStatusSuccess",
  GET_JEDECLARE_STATUS_ERROR = "JEDECLARE/getJedeclareStatusError",

  UPDATE_JEDECLARE_DATA_ATTEMPT = "JEDECLARE/updateJedeclareDataAttempt",
  UPDATE_JEDECLARE_DATA_SUCCESS = "JEDECLARE/updateJedeclareDataSuccess",
  UPDATE_JEDECLARE_DATA_ERROR = "JEDECLARE/updateJedeclareDataError",

  DISCONNECT_JEDECLARE_USER_ATTEMPT = "JEDECLARE/logoutJedeclareAttempt",
  DISCONNECT_JEDECLARE_USER_SUCCESS = "JEDECLARE/logoutJedeclareSuccess",
  DISCONNECT_JEDECLARE_USER_ERROR = "JEDECLARE/logoutJedeclareError",
}

export const connectJedeclareUserAttemptAction = () =>
  ({ type: JedeclareActionsEnum.CONNECT_JEDECLARE_USER_ATTEMPT } as const)
export const connectJedeclareUserSuccessAction = () =>
  ({
    type: JedeclareActionsEnum.CONNECT_JEDECLARE_USER_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "fiduciary.jedeclare.success.connect-jedeclare-user.title",
      bodyKey: "fiduciary.jedeclare.success.connect-jedeclare-user.body",
    },
  } as const)
export const connectJedeclareUserErrorAction = (error: ToastErrorProps) =>
  ({
    type: JedeclareActionsEnum.CONNECT_JEDECLARE_USER_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const getJedeclareStatusAttemptAction = () =>
  ({ type: JedeclareActionsEnum.GET_JEDECLARE_STATUS_ATTEMPT } as const)
export const getJedeclareStatusSuccessAction = (payload: StatusJedeclare) =>
  ({
    type: JedeclareActionsEnum.GET_JEDECLARE_STATUS_SUCCESS,
    payload,
  } as const)
export const getJedeclareStatusErrorAction = (error: ToastErrorProps) =>
  ({
    type: JedeclareActionsEnum.GET_JEDECLARE_STATUS_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const updateJedeclareDataAttemptAction = () =>
  ({ type: JedeclareActionsEnum.UPDATE_JEDECLARE_DATA_ATTEMPT } as const)
export const updateJedeclareDataSuccessAction = () =>
  ({
    type: JedeclareActionsEnum.UPDATE_JEDECLARE_DATA_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "fiduciary.jedeclare.success.update-jedeclare-data.title",
      bodyKey: "fiduciary.jedeclare.success.update-jedeclare-data.body",
    },
  } as const)
export const updateJedeclareDataErrorAction = (error: ToastErrorProps) =>
  ({
    type: JedeclareActionsEnum.UPDATE_JEDECLARE_DATA_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

export const disconnectJedeclareUserAttemptAction = () =>
  ({ type: JedeclareActionsEnum.DISCONNECT_JEDECLARE_USER_ATTEMPT } as const)
export const disconnectJedeclareUserSuccessAction = () =>
  ({
    type: JedeclareActionsEnum.DISCONNECT_JEDECLARE_USER_SUCCESS,
    withToast: true,
    toasterType: "success",
    message: {
      titleKey: "fiduciary.jedeclare.success.logout-jedeclare.title",
      bodyKey: "fiduciary.jedeclare.success.logout-jedeclare.body",
    },
  } as const)
export const disconnectJedeclareUserErrorAction = (error: ToastErrorProps) =>
  ({
    type: JedeclareActionsEnum.DISCONNECT_JEDECLARE_USER_ERROR,
    withToast: true,
    toasterType: "error",
    message: {
      titleKey: error.titleKey,
      bodyKey: error.bodyKey,
      bodyValues: error.bodyValues,
    },
  } as const)

type JedeclareActionType = ReturnType<
  | typeof connectJedeclareUserAttemptAction
  | typeof connectJedeclareUserSuccessAction
  | typeof connectJedeclareUserErrorAction
  | typeof getJedeclareStatusAttemptAction
  | typeof getJedeclareStatusSuccessAction
  | typeof getJedeclareStatusErrorAction
  | typeof updateJedeclareDataAttemptAction
  | typeof updateJedeclareDataSuccessAction
  | typeof updateJedeclareDataErrorAction
  | typeof disconnectJedeclareUserAttemptAction
  | typeof disconnectJedeclareUserSuccessAction
  | typeof disconnectJedeclareUserErrorAction
>

export interface JedeclareState {
  connectJedeclareUserStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  getJedeclareStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  updateJedeclareDataStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  disconnectJedeclareUserStatus: "IDLE" | "LOADING" | "SUCCESS" | "ERROR"
  connected: boolean
  user: User | null
  connectedCompaniesIds: number[]
}

export const jedeclareInitialState: JedeclareState = {
  connectJedeclareUserStatus: "IDLE",
  getJedeclareStatus: "IDLE",
  updateJedeclareDataStatus: "IDLE",
  disconnectJedeclareUserStatus: "IDLE",
  connected: false,
  user: null,
  connectedCompaniesIds: [],
}

export function jedeclareReducer(
  state = jedeclareInitialState,
  action: JedeclareActionType
): JedeclareState {
  switch (action.type) {
    case JedeclareActionsEnum.CONNECT_JEDECLARE_USER_ATTEMPT: {
      return { ...state, connectJedeclareUserStatus: "LOADING" }
    }
    case JedeclareActionsEnum.CONNECT_JEDECLARE_USER_SUCCESS: {
      return { ...state, connectJedeclareUserStatus: "SUCCESS" }
    }
    case JedeclareActionsEnum.CONNECT_JEDECLARE_USER_ERROR: {
      return { ...state, connectJedeclareUserStatus: "ERROR" }
    }
    case JedeclareActionsEnum.GET_JEDECLARE_STATUS_ATTEMPT: {
      return { ...state, getJedeclareStatus: "LOADING" }
    }
    case JedeclareActionsEnum.GET_JEDECLARE_STATUS_SUCCESS: {
      return {
        ...state,
        getJedeclareStatus: "SUCCESS",
        connected: action.payload.connected,
        user: action.payload.user,
        connectedCompaniesIds: action.payload.connected_companies_ids,
      }
    }
    case JedeclareActionsEnum.GET_JEDECLARE_STATUS_ERROR: {
      return { ...state, getJedeclareStatus: "ERROR" }
    }
    case JedeclareActionsEnum.UPDATE_JEDECLARE_DATA_ATTEMPT: {
      return { ...state, updateJedeclareDataStatus: "LOADING" }
    }
    case JedeclareActionsEnum.UPDATE_JEDECLARE_DATA_SUCCESS: {
      return { ...state, updateJedeclareDataStatus: "SUCCESS" }
    }
    case JedeclareActionsEnum.UPDATE_JEDECLARE_DATA_ERROR: {
      return { ...state, updateJedeclareDataStatus: "ERROR" }
    }
    case JedeclareActionsEnum.DISCONNECT_JEDECLARE_USER_ATTEMPT: {
      return { ...state, disconnectJedeclareUserStatus: "LOADING" }
    }
    case JedeclareActionsEnum.DISCONNECT_JEDECLARE_USER_SUCCESS: {
      return { ...state, disconnectJedeclareUserStatus: "SUCCESS" }
    }
    case JedeclareActionsEnum.DISCONNECT_JEDECLARE_USER_ERROR: {
      return { ...state, disconnectJedeclareUserStatus: "ERROR" }
    }

    default:
      return { ...state }
  }
}

const getJedeclareErrorMessages = (
  error: AxiosError | Error,
  defaultKey = "default"
) => {
  let key = defaultKey
  let bodyValues = {}

  if (
    isAxiosError(error) &&
    error.response !== undefined &&
    error.response.data
  ) {
    const errorMsg = JSON.stringify(error.response.data)

    if (errorMsg.includes("Invalid email format")) {
      key = "invalid-email-format"
    } else if (errorMsg.includes("password empty")) {
      key = "password-empty"
    } else if (
      errorMsg.includes("Not authenticated") ||
      errorMsg.includes("Unauthorized")
    ) {
      key = "invalid-credentials"
    }
  }

  return {
    titleKey: `fiduciary.jedeclare.error.${key}.title`,
    bodyKey: `fiduciary.jedeclare.error.${key}.body`,
    bodyValues,
  }
}

export const connectJedeclareUser =
  (fiduciaryId: number, email: string, password: string) =>
  (dispatch: Dispatch<JedeclareActionType | RNBThunkAction>) => {
    dispatch(connectJedeclareUserAttemptAction())

    const data = { email: email, password: password }

    return axios
      .post<StatusJedeclare>(
        `fiduciaries/${fiduciaryId}/connect_jedeclare_user`,
        data
      )
      .then(() => {
        dispatch(connectJedeclareUserSuccessAction())
        dispatch(getStatusJedeclare(fiduciaryId))
      })
      .catch((error: AxiosError) => {
        dispatch(
          connectJedeclareUserErrorAction(getJedeclareErrorMessages(error))
        )
      })
  }

export const getStatusJedeclare =
  (fiduciaryId: number) =>
  (dispatch: Dispatch<JedeclareActionType | RNBThunkAction>) => {
    dispatch(getJedeclareStatusAttemptAction())
    return axios
      .get<StatusJedeclare>(`fiduciaries/${fiduciaryId}/get_jedeclare_status`)
      .then((response) => {
        dispatch(getJedeclareStatusSuccessAction(response.data))
      })
      .catch((error: AxiosError) => {
        dispatch(
          getJedeclareStatusErrorAction(getJedeclareErrorMessages(error))
        )
      })
  }

export const updateJedeclareData =
  (fiduciaryId: number) =>
  (dispatch: Dispatch<JedeclareActionType | RNBThunkAction>) => {
    dispatch(updateJedeclareDataAttemptAction())

    return axios
      .put<StatusJedeclare>(`fiduciaries/${fiduciaryId}/update_jedeclare_data`)
      .then(() => {
        dispatch(updateJedeclareDataSuccessAction())
        dispatch(getStatusJedeclare(fiduciaryId))
      })
      .catch((error: AxiosError) => {
        dispatch(
          updateJedeclareDataErrorAction(getJedeclareErrorMessages(error))
        )
      })
  }

export const disconnectJedeclareUser =
  (fiduciaryId: number) =>
  (dispatch: Dispatch<JedeclareActionType | RNBThunkAction>) => {
    dispatch(disconnectJedeclareUserAttemptAction())

    return axios
      .put<StatusJedeclare>(
        `fiduciaries/${fiduciaryId}/disconnect_jedeclare_user`
      )
      .then(() => {
        dispatch(disconnectJedeclareUserSuccessAction())
        dispatch(getStatusJedeclare(fiduciaryId))
      })
      .catch((error: AxiosError) => {
        dispatch(
          disconnectJedeclareUserErrorAction(getJedeclareErrorMessages(error))
        )
      })
  }
